import {
  Timestamp,
  collection,
  doc,
  serverTimestamp,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { type Firestore } from 'firebase/firestore';
import { type Product, type Order, type OrderLine, type OrderStatus, type OrderSource, type ProductOption, type PortionSize, type CompositionComponent, type SaleMode, type WeightEstimate, type ServingEstimate, type MarketRequest, type MarketRequestStatus, type DayOfWeek, type RecurringOrder, type RecurringOrderLine, type RecurringOrderStatus, type CompositionSelection } from '../types';

// Types for data as it exists in Firestore (before conversion to app types)
interface FirestoreCompositionComponentData {
  productId: string;
  label: string;
}

interface FirestoreProductOptionData {
  id: string;
  label: string;
  type: string;
  choices?: Array<{id: string; label: string; priceModifier?: number | null}> | null;
  maxSelections?: number | null;
  required?: boolean | null;                                 // For select type: mandatory or optional
  components?: FirestoreCompositionComponentData[] | null;  // For composition type
  totalRequired?: number | null;                             // For composition type
}

interface FirestorePortionSizeData {
  id: string;
  label: string;
  price: number;
}

interface FirestoreWeightEstimateData {
  averageWeight: number;
  servesPerUnit?: { min: number; max: number } | null;
}

interface FirestoreServingEstimateData {
  gramsPerPerson: number;
}

interface FirestoreProductData {
  name: string;
  shortenName: string | null;
  description: string | null;
  picture: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  imageUpdatedAt: string | null;
  section: string | null;
  subSection: string | null;
  notes: string | null;
  displayOrder: number | null;
  tenantId: string | null;
  outOfStockDate: string | null;
  isActive: boolean | null;

  // Pricing fields
  saleMode: SaleMode | null;
  price: number | null;

  // Mode-specific fields
  weightEstimate: FirestoreWeightEstimateData | null;
  servingEstimate: FirestoreServingEstimateData | null;
  portionSizes: FirestorePortionSizeData[] | null;

  // Common fields
  disclaimer: string | null;
  availableOptions: FirestoreProductOptionData[] | null;

  // Market availability
  marketIds: string[] | null;

  // Legacy field that might exist in old documents
  unit?: string;
}

interface FirestoreOrderLineData {
  productId: string;
  quantity: number;
  preparedQuantity: number;
  options: Array<{id: string; value: boolean | string | string[] | CompositionSelection[]}> | null;
  portionSizeId?: string | null;
  // Sale mode fields (Phase 5)
  saleMode?: SaleMode | null;
  servings?: number | null;
  estimatedWeight?: number | null;
  estimatedPrice?: number | null;
  finalWeight?: number | null;
  finalPrice?: number | null;
}

interface FirestoreOrderData {
  customerName: string;
  phoneNumber: string | null;
  pickupAtISO: string;
  pickupDate: string;
  lines: FirestoreOrderLineData[];
  status: OrderStatus;
  paid: boolean;
  note: string | null;
  fcmToken: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  tenantId: string | null;
  marketId: string | null;
  customerId: string | null;
  source: OrderSource | null;
  recurringOrderId: string | null;
}

interface FirestoreRecurringOrderLineData {
  productId: string;
  quantity: number;
  options: Array<{id: string; value: boolean | string | string[] | CompositionSelection[]}> | null;
  portionSizeId: string | null;
}

interface FirestoreRecurringOrderData {
  tenantId: string;
  marketId: string;
  customerName: string;
  phoneNumber: string | null;
  preferredPickupTime: string;
  lines: FirestoreRecurringOrderLineData[];
  status: RecurringOrderStatus;
  note: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastGeneratedDate: string | null;
}

export function productsCollection(db: Firestore) {
  return collection(db, 'products').withConverter(productConverter);
}

export function productDoc(db: Firestore, id: string) {
  return doc(db, 'products', id).withConverter(productConverter);
}

export function ordersCollection(db: Firestore) {
  return collection(db, 'orders').withConverter(orderConverter);
}

export function orderDoc(db: Firestore, id: string) {
  return doc(db, 'orders', id).withConverter(orderConverter);
}

export function recurringOrdersCollection(db: Firestore) {
  return collection(db, 'recurringOrders').withConverter(recurringOrderConverter);
}

export function recurringOrderDoc(db: Firestore, id: string) {
  return doc(db, 'recurringOrders', id).withConverter(recurringOrderConverter);
}

// Helper to convert ProductOption to Firestore format (undefined -> null)
function toFirestoreProductOption(opt: ProductOption): FirestoreProductOptionData {
  return {
    id: opt.id,
    label: opt.label,
    type: opt.type,
    choices: opt.type === 'select' || opt.type === 'multiselect'
      ? (opt.choices?.map(c => ({ id: c.id, label: c.label, priceModifier: c.priceModifier ?? null })) ?? null)
      : null,
    maxSelections: opt.type === 'multiselect' ? (opt.maxSelections ?? null) : null,
    required: opt.type === 'select' ? (opt.required ?? null) : null,
    components: opt.type === 'composition'
      ? (opt.components?.map(c => ({ productId: c.productId, label: c.label })) ?? null)
      : null,
    totalRequired: opt.type === 'composition' ? (opt.totalRequired ?? null) : null,
  };
}

// Helper to convert PortionSize to Firestore format
function toFirestorePortionSize(ps: PortionSize): FirestorePortionSizeData {
  return {
    id: ps.id,
    label: ps.label,
    price: ps.price,
  };
}

// Helper to convert WeightEstimate to Firestore format
function toFirestoreWeightEstimate(we: WeightEstimate): FirestoreWeightEstimateData {
  return {
    averageWeight: we.averageWeight,
    servesPerUnit: we.servesPerUnit ?? null,
  };
}

// Helper to convert ServingEstimate to Firestore format
function toFirestoreServingEstimate(se: ServingEstimate): FirestoreServingEstimateData {
  return {
    gramsPerPerson: se.gramsPerPerson,
  };
}

const productConverter: FirestoreDataConverter<Product> = {
  toFirestore(product: Product) {
    return {
      name: product.name,
      shortenName: product.shortenName ?? null,
      description: product.description ?? null,
      picture: product.picture ?? null,
      imageUrl: product.imageUrl ?? null,
      thumbnailUrl: product.thumbnailUrl ?? null,
      imageUpdatedAt: product.imageUpdatedAt ?? null,
      section: product.section ?? null,
      subSection: product.subSection ?? null,
      notes: product.notes ?? null,
      displayOrder: product.displayOrder ?? null,
      tenantId: product.tenantId ?? null,
      outOfStockDate: product.outOfStockDate ?? null,
      isActive: product.isActive ?? null,

      // Pricing fields
      saleMode: product.saleMode ?? null,
      price: product.price ?? null,

      // Mode-specific fields
      weightEstimate: product.weightEstimate ? toFirestoreWeightEstimate(product.weightEstimate) : null,
      servingEstimate: product.servingEstimate ? toFirestoreServingEstimate(product.servingEstimate) : null,
      portionSizes: product.portionSizes?.map(toFirestorePortionSize) ?? null,

      // Common fields
      disclaimer: product.disclaimer ?? null,
      availableOptions: product.availableOptions?.map(toFirestoreProductOption) ?? null,

      // Market availability
      marketIds: product.marketIds && product.marketIds.length > 0 ? product.marketIds : null,
      // server timestamps when created via services
    };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): Product {
    const data = snapshot.data() as FirestoreProductData;

    // Convert availableOptions to proper type
    const availableOptions: ProductOption[] | undefined = data.availableOptions?.map(opt => {
      const baseOption = {
        id: opt.id,
        label: opt.label,
      };

      // Handle different option types
      if (opt.type === 'select' || opt.type === 'multiselect') {
        return {
          ...baseOption,
          type: opt.type as 'select' | 'multiselect',
          choices: opt.choices?.map(c => ({ id: c.id, label: c.label, ...(c.priceModifier != null ? { priceModifier: c.priceModifier } : {}) })) ?? [],
          ...(opt.type === 'multiselect' && opt.maxSelections ? { maxSelections: opt.maxSelections } : {}),
          ...(opt.type === 'select' && opt.required != null ? { required: opt.required } : {}),
        };
      }

      // Handle composition type
      if (opt.type === 'composition') {
        const components: CompositionComponent[] = opt.components?.map(c => ({
          productId: c.productId,
          label: c.label,
        })) ?? [];
        return {
          ...baseOption,
          type: 'composition' as const,
          components,
          ...(opt.totalRequired != null ? { totalRequired: opt.totalRequired } : {}),
        };
      }

      // Default to boolean for legacy data
      return {
        ...baseOption,
        type: 'boolean' as const,
      };
    });

    // Convert portionSizes to proper type
    const portionSizes: PortionSize[] | undefined = data.portionSizes?.map(ps => ({
      id: ps.id,
      label: ps.label,
      price: ps.price,
    }));

    // Convert weightEstimate from Firestore format
    const weightEstimate: WeightEstimate | undefined = data.weightEstimate
      ? {
          averageWeight: data.weightEstimate.averageWeight,
          ...(data.weightEstimate.servesPerUnit ? { servesPerUnit: data.weightEstimate.servesPerUnit } : {}),
        }
      : undefined;

    // Convert servingEstimate from Firestore format
    const servingEstimate: ServingEstimate | undefined = data.servingEstimate
      ? {
          gramsPerPerson: data.servingEstimate.gramsPerPerson,
        }
      : undefined;

    return {
      id: snapshot.id,
      name: data.name,
      shortenName: data.shortenName ?? undefined,
      description: data.description ?? undefined,
      picture: data.picture ?? undefined,
      imageUrl: data.imageUrl ?? undefined,
      thumbnailUrl: data.thumbnailUrl ?? undefined,
      imageUpdatedAt: data.imageUpdatedAt ?? undefined,
      section: data.section ?? undefined,
      subSection: data.subSection ?? undefined,
      notes: data.notes ?? undefined,
      displayOrder: data.displayOrder ?? undefined,
      tenantId: data.tenantId ?? undefined,
      outOfStockDate: data.outOfStockDate ?? undefined,
      isActive: data.isActive ?? true,

      // Pricing fields
      saleMode: data.saleMode ?? 'UNIT', // Fallback for safety
      price: data.price ?? undefined,

      // Mode-specific fields
      weightEstimate,
      servingEstimate,
      portionSizes: portionSizes ?? undefined,

      // Common fields
      disclaimer: data.disclaimer ?? undefined,
      availableOptions: availableOptions ?? undefined,

      // Market availability
      marketIds: data.marketIds && data.marketIds.length > 0 ? data.marketIds : undefined,
    };
  },
};

function toFirestoreOrderLine(line: OrderLine) {
  return {
    productId: line.productId,
    quantity: line.quantity,
    preparedQuantity: line.preparedQuantity,
    options: line.options ?? null,
    portionSizeId: line.portionSizeId ?? null,
    // Sale mode fields (Phase 5)
    saleMode: line.saleMode ?? null,
    servings: line.servings ?? null,
    estimatedWeight: line.estimatedWeight ?? null,
    estimatedPrice: line.estimatedPrice ?? null,
    finalWeight: line.finalWeight ?? null,
    finalPrice: line.finalPrice ?? null,
  };
}

function fromFirestoreOrderLine(data: FirestoreOrderLineData): OrderLine {
  // Prefer new options array, but fall back to legacy fields
  const options = data.options && Array.isArray(data.options) && data.options.length > 0
    ? data.options
    : undefined;

  return {
    productId: data.productId,
    quantity: data.quantity,
    preparedQuantity: data.preparedQuantity ?? 0,
    options: options,
    portionSizeId: data.portionSizeId ?? undefined,
    // Sale mode fields (Phase 5)
    saleMode: data.saleMode ?? undefined,
    servings: data.servings ?? undefined,
    estimatedWeight: data.estimatedWeight ?? undefined,
    estimatedPrice: data.estimatedPrice ?? undefined,
    finalWeight: data.finalWeight ?? undefined,
    finalPrice: data.finalPrice ?? undefined,
  };
}

const orderConverter: FirestoreDataConverter<Order> = {
  toFirestore(order: Order) {
    return {
      customerName: order.customerName,
      phoneNumber: order.phoneNumber ?? null,
      pickupAtISO: order.pickupAtISO,
      pickupDate: order.pickupDate,
      lines: order.lines.map(toFirestoreOrderLine),
      status: order.status,
      paid: order.paid || false,
      note: order.note || null,
      fcmToken: order.fcmToken || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      tenantId: order.tenantId ?? null,
      marketId: order.marketId ?? null,
      customerId: order.customerId ?? null,
      source: order.source ?? null,
      recurringOrderId: order.recurringOrderId ?? null,
    };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): Order {
    const data = snapshot.data() as FirestoreOrderData;
    const createdAt = (data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date()).toISOString();
    const updatedAt = (data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()).toISOString();
    return {
      id: snapshot.id,
      customerName: data.customerName,
      phoneNumber: data.phoneNumber ?? undefined,
      pickupAtISO: data.pickupAtISO,
      pickupDate: data.pickupDate,
      lines: Array.isArray(data.lines) ? data.lines.map(fromFirestoreOrderLine) : [],
      status: data.status || 'pending',
      paid: data.paid || false,
      note: data.note || undefined,
      fcmToken: data.fcmToken || undefined,
      createdAtISO: createdAt,
      updatedAtISO: updatedAt,
      tenantId: data.tenantId ?? undefined,
      marketId: data.marketId ?? undefined,
      customerId: data.customerId ?? undefined,
      source: data.source ?? undefined,
      recurringOrderId: data.recurringOrderId ?? undefined,
    };
  },
};

// ===== RECURRING ORDERS =====

const recurringOrderConverter: FirestoreDataConverter<RecurringOrder> = {
  toFirestore(order: RecurringOrder) {
    return {
      tenantId: order.tenantId,
      marketId: order.marketId,
      customerName: order.customerName,
      phoneNumber: order.phoneNumber ?? null,
      preferredPickupTime: order.preferredPickupTime,
      lines: order.lines.map((l): FirestoreRecurringOrderLineData => ({
        productId: l.productId,
        quantity: l.quantity,
        options: l.options ?? null,
        portionSizeId: l.portionSizeId ?? null,
      })),
      status: order.status,
      note: order.note ?? null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastGeneratedDate: order.lastGeneratedDate ?? null,
    };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): RecurringOrder {
    const data = snapshot.data() as FirestoreRecurringOrderData;
    const createdAt = (data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date()).toISOString();
    const updatedAt = (data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()).toISOString();
    return {
      id: snapshot.id,
      tenantId: data.tenantId,
      marketId: data.marketId,
      customerName: data.customerName,
      phoneNumber: data.phoneNumber ?? undefined,
      preferredPickupTime: data.preferredPickupTime,
      lines: Array.isArray(data.lines) ? data.lines.map((l): RecurringOrderLine => ({
        productId: l.productId,
        quantity: l.quantity,
        options: l.options && l.options.length > 0 ? l.options : undefined,
        portionSizeId: l.portionSizeId ?? undefined,
      })) : [],
      status: data.status,
      note: data.note ?? undefined,
      createdAtISO: createdAt,
      updatedAtISO: updatedAt,
      lastGeneratedDate: data.lastGeneratedDate ?? undefined,
    };
  },
};

// ===== MARKET REQUEST =====

interface FirestoreMarketRequestData {
  requestedBy: string;
  requestedByEmail: string;
  tenantId: string | null;
  city: string;
  dayOfWeek: number;
  status: MarketRequestStatus;
  createdAt: Timestamp;
  reviewedAt: Timestamp | null;
  reviewedBy: string | null;
  marketId: string | null;
}

export function marketRequestsCollection(db: Firestore) {
  return collection(db, 'marketRequests').withConverter(marketRequestConverter);
}

export function marketRequestDoc(db: Firestore, id: string) {
  return doc(db, 'marketRequests', id).withConverter(marketRequestConverter);
}

const marketRequestConverter: FirestoreDataConverter<MarketRequest> = {
  toFirestore(request: MarketRequest) {
    return {
      requestedBy: request.requestedBy,
      requestedByEmail: request.requestedByEmail,
      tenantId: request.tenantId ?? null,
      city: request.city,
      dayOfWeek: request.dayOfWeek,
      status: request.status,
      createdAt: serverTimestamp(),
      reviewedAt: request.reviewedAt ? Timestamp.fromDate(request.reviewedAt) : null,
      reviewedBy: request.reviewedBy ?? null,
      marketId: request.marketId ?? null,
    };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): MarketRequest {
    const data = snapshot.data() as FirestoreMarketRequestData;
    return {
      id: snapshot.id,
      requestedBy: data.requestedBy,
      requestedByEmail: data.requestedByEmail,
      tenantId: data.tenantId ?? undefined,
      city: data.city,
      dayOfWeek: data.dayOfWeek as DayOfWeek,
      status: data.status,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
      reviewedAt: data.reviewedAt instanceof Timestamp ? data.reviewedAt.toDate() : undefined,
      reviewedBy: data.reviewedBy ?? undefined,
      marketId: data.marketId ?? undefined,
    };
  },
};


